import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { socket } from '../socket';
import PatternPreview from '../components/PatternPreview';
import { styles } from './BingoCardPage.styles';

const PATTERN_LABELS = {
  line:         { label: 'Line',         description: 'Any row, column, or diagonal' },
  blackout:     { label: 'Blackout',     description: 'Every square' },
  four_corners: { label: 'Four Corners', description: 'All four corners' },
  x:            { label: 'X',            description: 'Both diagonals' },
  plus:         { label: 'Plus',         description: 'Center row and column' },
  t:            { label: 'T',            description: 'Top row and center column' },
  l:            { label: 'L',            description: 'Left column and bottom row' },
  frame:        { label: 'Frame',        description: 'All border squares' },
};

// The current clue is the last called word, while the game is in play.
function currentClue(calledWords, status) {
  const inPlay = status === 'active' || status === 'paused';
  return inPlay && calledWords?.length ? calledWords[calledWords.length - 1] : null;
}

// Whether the student already marked the current clue's cell.
function clueMarked(board, markedCells, clue) {
  const idx = clue ? board.findIndex((c) => c && c.word === clue.word) : -1;
  return idx !== -1 && markedCells.includes(idx);
}

export default function BingoCardPage() {
  const { code } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  // If student lands here without going through JoinPage (e.g. direct URL), send them to join
  useEffect(() => {
    if (!state?.board) {
      navigate(`/join?code=${code}`, { replace: true });
    }
  }, [state, code, navigate]);

  const [boardSize, setBoardSize] = useState(state?.boardSize || 5);
  const [freeSpace, setFreeSpace] = useState(state?.freeSpace !== false);
  const [displayMode, setDisplayMode] = useState(state?.displayMode || 'word');
  const [pattern, setPattern] = useState(state?.pattern || 'line');
  const [novelTitle] = useState(state?.novelTitle || 'Vocabulary Bingo');
  const [board, setBoard] = useState(state?.board || []);
  const [marked, setMarked] = useState(() => {
    const init = new Set(state?.markedCells || []);
    const size = state?.boardSize || 5;
    const free = state?.freeSpace !== false;
    if (size === 5 && free) init.add(12);
    return init;
  });
  const [calledWords, setCalledWords] = useState(state?.calledWords || []);
  const [lastCalled, setLastCalled] = useState(() => currentClue(state?.calledWords, state?.status));
  const [gameStatus, setGameStatus] = useState(state?.status || 'waiting');
  const [winner, setWinner] = useState(null);
  const [isWinner, setIsWinner] = useState(false); // the latest announced winner is me
  const [hasWon, setHasWon] = useState(state?.hasWon || false); // one bingo per round
  const [winningLine, setWinningLine] = useState(null);
  const [connected, setConnected] = useState(socket.connected);
  const [teacherAway, setTeacherAway] = useState(false);

  // Three-strike state
  const [strikes, setStrikes] = useState(state?.strikes || 0);
  const [lockedOut, setLockedOut] = useState(state?.lockedOut || false);
  // correct cell chosen for current word
  const [wordMarked, setWordMarked] = useState(() =>
    clueMarked(state?.board || [], state?.markedCells || [], currentClue(state?.calledWords, state?.status)));
  // shakeTarget: { idx, seq } — seq alternates 0/1 to retrigger animation on same cell
  const [shakeTarget, setShakeTarget] = useState(null);
  const [lastMarkedIdx, setLastMarkedIdx] = useState(null);
  // cell indices guessed incorrectly for the current word — reset on each new clue
  const [wrongCells, setWrongCells] = useState(() => new Set());
  // increments on each wrong click to retrigger the overlay fade animation
  const [strikeOverlayKey, setStrikeOverlayKey] = useState(0);
  const [showStrikeOverlay, setShowStrikeOverlay] = useState(false);
  // increments on each invalid bingo claim to retrigger its overlay fade
  const [invalidBingoKey, setInvalidBingoKey] = useState(0);
  const [showInvalidOverlay, setShowInvalidOverlay] = useState(false);

  // True when this socket must (re-)join the room: on mount after a page
  // refresh (new, unconnected socket) and after every disconnect.
  const needsRejoinRef = useRef(!socket.connected);

  // Re-join with the token from the original join so the server restores this
  // student's board, then replace local state with the server's (Spec §5/§6).
  const rejoin = useCallback(() => {
    socket.emit('game:join', { code, playerName: state?.playerName, rejoinToken: state?.rejoinToken }, (res) => {
      if (res?.error) {
        setGameStatus('ended');
        setWinner(null);
        return;
      }
      const size = res.boardSize || 5;
      const free = res.freeSpace !== false;
      const restored = new Set(res.markedCells || []);
      if (size === 5 && free) restored.add(12);
      const clue = currentClue(res.calledWords, res.status);
      setBoard(res.board);
      setBoardSize(size);
      setFreeSpace(free);
      setDisplayMode(res.displayMode || 'word');
      setPattern(res.pattern || 'line');
      setMarked(restored);
      setCalledWords(res.calledWords || []);
      setLastCalled(clue);
      setWordMarked(clueMarked(res.board, res.markedCells || [], clue));
      setStrikes(res.strikes || 0);
      setLockedOut(res.lockedOut || false);
      setHasWon(res.hasWon || false);
      setWrongCells(new Set());
      setGameStatus(res.status);
    });
  }, [code, state]);

  const handleConnect = useCallback(() => {
    setConnected(true);
    if (needsRejoinRef.current) {
      needsRejoinRef.current = false;
      rejoin();
    }
  }, [rejoin]);
  const handleDisconnect = useCallback(() => {
    setConnected(false);
    needsRejoinRef.current = true;
  }, []);

  useEffect(() => {
    if (!state?.board) return;

    // After a page refresh the socket is new and unconnected; handleConnect rejoins
    if (!socket.connected) socket.connect();

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    socket.on('game:started', ({ calledWords: cw }) => {
      setCalledWords(cw || []);
      setGameStatus('active');
    });

    socket.on('game:word_called', ({ word, definition }) => {
      setCalledWords((prev) => [...prev, { word, definition }]);
      setLastCalled({ word, definition });
      // Reset strike/lock state when a new word is called
      setStrikes(0);
      setLockedOut(false);
      setWordMarked(false);
      setShakeTarget(null);
      setShowStrikeOverlay(false);
      setWrongCells(new Set());
    });

    // A bingo pauses the game until the teacher continues or ends it
    socket.on('game:bingo', ({ winner: w, winnerSocketId, winningLine: line }) => {
      const iMe = winnerSocketId === socket.id;
      setWinner(w);
      setIsWinner(iMe);
      // Only highlight the winning pattern on the winner's own board
      if (iMe) {
        setHasWon(true);
        setWinningLine(line || null);
      }
      setGameStatus('paused');
    });

    socket.on('game:resumed', () => {
      setWinner(null);
      setGameStatus('active');
    });

    socket.on('game:ended', () => {
      setGameStatus('ended');
      setWinner(null);
    });

    socket.on('game:teacher_disconnected', () => {
      setTeacherAway(true);
    });

    socket.on('game:teacher_reconnected', () => {
      setTeacherAway(false);
    });

    socket.on('game:kicked', () => {
      socket.disconnect();
      navigate(`/join?code=${code}&kicked=1`, { replace: true });
    });

    // Teacher started a new round — server sends each student a fresh board
    socket.on('game:reset', ({ board: newBoard, boardSize: newSize, freeSpace: newFree, displayMode: newMode, pattern: newPattern }) => {
      const size = newSize || 5;
      const free = newFree !== false;
      setBoard(newBoard);
      setBoardSize(size);
      setFreeSpace(free);
      setDisplayMode(newMode || 'word');
      setPattern(newPattern || 'line');
      const initMarked = new Set();
      if (size === 5 && free) initMarked.add(12);
      setMarked(initMarked);
      setCalledWords([]);
      setLastCalled(null);
      setWinner(null);
      setIsWinner(false);
      setHasWon(false);
      setWinningLine(null);
      setStrikes(0);
      setLockedOut(false);
      setWordMarked(false);
      setShakeTarget(null);
      setLastMarkedIdx(null);
      setShowStrikeOverlay(false);
      setShowInvalidOverlay(false);
      setWrongCells(new Set());
      setGameStatus('waiting');
    });

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('game:started');
      socket.off('game:word_called');
      socket.off('game:bingo');
      socket.off('game:resumed');
      socket.off('game:ended');
      socket.off('game:teacher_disconnected');
      socket.off('game:teacher_reconnected');
      socket.off('game:kicked');
      socket.off('game:reset');
    };
  }, [state, code, navigate, handleConnect, handleDisconnect]);

  const inPlay = gameStatus === 'active' || gameStatus === 'paused';

  function handleCellClick(idx) {
    if (!inPlay) return;
    if (boardSize === 5 && freeSpace && idx === 12) return; // FREE
    if (marked.has(idx)) {
      // Clicking a marked square un-marks it (Spec §5)
      socket.emit('game:unmark_cell', { cellIdx: idx }, (res) => {
        if (!res?.unmarked) return;
        setMarked((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
        // Un-marking the current clue's square lets the student mark it again
        if (lastCalled && board[idx]?.word === lastCalled.word) setWordMarked(false);
      });
      return;
    }
    if (lockedOut || wordMarked) return;
    if (wrongCells.has(idx)) return; // already guessed wrong for this word
    if (!board[idx]) return;

    socket.emit('game:mark_cell', { cellIdx: idx }, (res) => {
      if (res?.marked) {
        setMarked((prev) => new Set([...prev, idx]));
        setStrikes(0);
        setLockedOut(false);
        setWordMarked(true);
        setLastMarkedIdx(idx);
        setTimeout(() => setLastMarkedIdx(null), 350);
      } else if (!res?.error) {
        // Wrong cell — show shake + update strike state
        const newStrikes = res.strikes;
        const nowLocked = res.lockedOut;
        setStrikes(newStrikes);
        setLockedOut(nowLocked);
        setWrongCells((prev) => new Set([...prev, idx]));
        setStrikeOverlayKey((k) => k + 1);
        setShowStrikeOverlay(true);
        setShakeTarget((prev) => ({ idx, seq: ((prev?.seq || 0) + 1) % 2 }));
        setTimeout(() => setShakeTarget(null), 450);
      }
    });
  }

  function handleClaimBingo() {
    const markedArr = [...marked];
    socket.emit('game:claim_bingo', { markedCells: markedArr }, (res) => {
      if (res?.valid) {
        setWinningLine(res.winningLine || null);
      } else {
        setInvalidBingoKey((k) => k + 1);
        setShowInvalidOverlay(true);
      }
    });
  }

  const isWinningCell = (idx) => winningLine && winningLine.includes(idx);
  const chancesLeft = 3 - strikes;

  if (!state?.board) return null;

  return (
    <div style={styles.page}>
      {/* Shake animation keyframes */}
      <style>{`
        @keyframes cellShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-7px); }
          40% { transform: translateX(7px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .shake-0, .shake-1 { animation: cellShake 0.45s ease; }
        @keyframes strikeOverlayFade {
          0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
          12%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          70%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>

      {/* ── Waiting state ───────────────────────────────────────── */}
      {gameStatus === 'waiting' && (
        <div style={styles.waitingOverlay}>
          <div style={styles.waitingCard}>
            Waiting for the teacher to start the game…
          </div>
        </div>
      )}

      {/* ── Winner / end banner ─────────────────────────────────── */}
      {(gameStatus === 'paused' || gameStatus === 'ended') && (
        <div className={winner ? 'animate-bingo-pop' : ''} style={winner ? styles.winnerBanner : styles.endedBanner}>
          {winner
            ? isWinner
              ? 'You got BINGO!'
              : `${winner} got BINGO!`
            : 'Game over.'}
        </div>
      )}

      {/* Header info row — full width */}
      <div style={styles.cardHeaderRow}>
        {state.teacherName && <span style={styles.cardMeta}>{state.teacherName}</span>}
        <span style={styles.cardMeta}>Room: {code}</span>
        <span style={styles.cardMeta}>{novelTitle}</span>
      </div>

      {/* Current clue — full width */}
      {lastCalled && inPlay && (
        <div style={styles.clueCard}>
          <div>
            <div style={styles.clueCardLabel}>CURRENT CLUE</div>
            <div style={styles.clueCardWord}>
              {displayMode === 'definition' ? lastCalled.word : lastCalled.definition}
            </div>
          </div>
          <div style={styles.winPattern}>
            <div style={styles.winPatternLabel}>WIN PATTERN</div>
            <PatternPreview pattern={pattern} size={boardSize} freeSpace={freeSpace} cellSize={6} />
            <div style={styles.winPatternName}>{PATTERN_LABELS[pattern]?.label}</div>
          </div>
        </div>
      )}

      <div style={styles.layout}>
        {/* ── Bingo card ────────────────────────────────────────── */}
        <div style={styles.cardWrap}>
          {/* Grid */}
          <div style={styles.gridWrap}>
            <div style={{
              ...styles.grid,
              gridTemplateColumns: `repeat(${boardSize}, 1fr)`,
              ...(lockedOut ? styles.gridLocked : {}),
            }}>
              {board.map((cell, idx) => {
                const isFree = boardSize === 5 && freeSpace && idx === 12;
                const isMarked = marked.has(idx);
                const isWin = isWinningCell(idx);
                const isShaking = shakeTarget?.idx === idx;
                const isJustMarked = lastMarkedIdx === idx;
                const isWrong = wrongCells.has(idx);
                const shakeClass = isShaking ? `shake-${shakeTarget.seq}` : '';

                return (
                  <button
                    key={idx}
                    className={[shakeClass, isJustMarked ? 'animate-stamp' : ''].filter(Boolean).join(' ')}
                    onClick={() => handleCellClick(idx)}
                    disabled={isFree || isWrong || !inPlay}
                    style={{
                      ...styles.cell,
                      ...(isMarked ? styles.cellMarked : {}),
                      ...(isWin ? styles.cellWin : {}),
                      ...(isFree ? styles.cellFree : {}),
                      ...(isWrong && !isMarked ? styles.cellWrong : {}),
                      ...(lockedOut && !isFree && !isMarked ? styles.cellLocked : {}),
                    }}
                  >
                    {isFree ? (
                      <span style={styles.freeLabel}>FREE</span>
                    ) : (
                      <>
                        <span style={{ ...styles.cellWord, ...(displayMode === 'definition' ? styles.cellWordDef : {}) }}>
                          {displayMode === 'definition' ? cell?.definition : cell?.word}
                        </span>
                        {isMarked && <span style={styles.checkmark}>✓</span>}
                        {isWrong && !isMarked && <span style={styles.xmark}>✗</span>}
                      </>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ── Invalid bingo overlay ────────────────────────────── */}
            {showInvalidOverlay && (
              <div
                key={invalidBingoKey}
                onAnimationEnd={() => setShowInvalidOverlay(false)}
                style={{
                  ...styles.strikeOverlay,
                  ...styles.invalidBingoOverlay,
                  animation: 'strikeOverlayFade 3s ease forwards',
                }}
              >
                <span style={styles.attemptsText}>Not quite — keep checking your board!</span>
              </div>
            )}

            {/* ── Strike overlay ────────────────────────────────────── */}
            {showStrikeOverlay && inPlay && (
              <div
                key={strikeOverlayKey}
                onAnimationEnd={() => setShowStrikeOverlay(false)}
                style={{
                  ...styles.strikeOverlay,
                  animation: `strikeOverlayFade ${lockedOut ? 3 : 2}s ease forwards`,
                }}
              >
                <div style={styles.attemptsDots}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={i < chancesLeft ? styles.dotFilled : styles.dotEmpty}>●</span>
                  ))}
                </div>
                <span style={styles.attemptsText}>
                  {lockedOut
                    ? 'Wait for the next word.'
                    : `${chancesLeft} chance${chancesLeft === 1 ? '' : 's'} left`}
                </span>
              </div>
            )}
          </div>

          {/* BINGO button */}
          {inPlay && !hasWon && (
            <div style={{ marginTop: '8px', flexShrink: 0 }}>
              <button onClick={handleClaimBingo} style={styles.bingoBtn}>BINGO!</button>
            </div>
          )}
        </div>

        {/* ── Called words sidebar ──────────────────────────────── */}
        <div style={styles.sidebar}>
          <h2 style={styles.sidebarHeading}>Called Words ({calledWords.length})</h2>
          {calledWords.length === 0 ? (
            <p style={styles.emptyText}>None yet.</p>
          ) : (
            <ul style={styles.calledList}>
              {[...calledWords].reverse().map((w, i) => (
                <li key={i} style={styles.calledItem}>
                  <span style={styles.calledWord}>
                    {displayMode === 'definition' ? w.word : w.definition}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>


      {gameStatus === 'ended' && (
        <div style={styles.claimRow}>
          <button
            onClick={() => { socket.disconnect(); navigate('/join'); }}
            style={styles.newGameBtn}
          >
            Play Again
          </button>
        </div>
      )}

      {teacherAway && (
        <div style={styles.teacherAwayOverlay}>
          <div style={styles.teacherAwayCard}>
            <div style={styles.teacherAwayIcon}>⏳</div>
            <div style={styles.teacherAwayTitle}>Teacher disconnected</div>
            <div style={styles.teacherAwayText}>Waiting for the teacher to reconnect…</div>
          </div>
        </div>
      )}

      {!connected && (
        <div style={styles.disconnectBanner}>
          Connection lost — waiting to reconnect…
        </div>
      )}
    </div>
  );
}
